import React, { useState } from 'react';
import { PeerJurisdictionCard } from './PeerJurisdictionCard';
import { AddPeerDialog } from './AddPeerDialog';
import { Place } from '@/contexts/PlaceNamesContext';
import { formatPlaceName } from '@/lib/utils';

export interface StepSectionProps {
  selectedPlace: Place | null;
  displayName: string;
  peers: Place[];
  loadingPeers: boolean;
  peersError: string | null;
  onAddPeer: (peer: Place) => void;
  onRemovePeer: (placeFips: string) => void;
}

export const StepSection: React.FC<StepSectionProps> = ({
  selectedPlace,
  displayName,
  peers,
  loadingPeers,
  peersError,
  onAddPeer,
  onRemovePeer
}) => {
  const [removingIndex, setRemovingIndex] = useState<number | null>(null);
  const [openDialogIndex, setOpenDialogIndex] = useState<number | null>(null);

  const handleAddJurisdiction = (place: Place) => {
    onAddPeer(place);
    setOpenDialogIndex(null);
  };

  const handleOpenAddDialog = (index: number) => {
    setOpenDialogIndex(index);
  };

  const handleCloseAddDialog = () => {
    setOpenDialogIndex(null);
  };

  const handleRemoveJurisdiction = (index: number) => {
    setRemovingIndex(index);
    
    // Wait for the removal animation to complete before updating state
    setTimeout(() => {
      const peer = peers[index];
      if (peer) {
        onRemovePeer(peer.place_fips);
      }
      setRemovingIndex(null);
    }, 300); // Match the animation duration
  };

  return (
    <>
      <section className="bg-white w-full">
        <div className="max-w-[1920px] mx-auto px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24 2xl:px-32 py-8 sm:py-10 md:py-12">
          <div className="w-full font-medium leading-[1.3]">
            {/* Step 1 Section */}
            <div className="w-full text-[#212121] font-semibold">
              <div className="font-inter text-sm sm:text-base tracking-[0.32px] uppercase font-semibold leading-[130%]">
                STEP 1
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-[32px] mt-3 leading-[1.3] font-inter">
                Select Peer Jurisdictions to Compare
              </h2>
              <p className="w-full text-lg sm:text-xl text-[rgba(33,33,33,1)] font-normal mt-3 leading-[1.3] font-inter">
                You may select up to 5 Peer Jurisdictions to compare against.
                We've already identified some for you below to get you started!
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-8 grid-flow-row h-full auto-rows-fr">
              {peers.map((jurisdiction, index) => (
                <PeerJurisdictionCard
                  key={jurisdiction.place_fips}
                  jurisdiction={jurisdiction}
                  onRemove={() => handleRemoveJurisdiction(index)}
                  isRemoving={removingIndex === index}
                />
              ))}
              {Array.from({ length: Math.max(0, 5 - peers.length) }).map((_, i) => (
                <AddPeerDialog
                  key={`add-${i}`}
                  open={openDialogIndex === peers.length + i}
                  onOpenChange={(open) => {
                    if (open) {
                      handleOpenAddDialog(peers.length + i);
                    } else {
                      handleCloseAddDialog();
                    }
                  }}
                  onAddJurisdiction={handleAddJurisdiction}
                  selectedPeers={peers} // Pass the currently selected peers
                >
                  <div
                    className="relative h-full min-h-[80px] rounded-lg cursor-pointer transition-all duration-300 overflow-hidden group"
                    onClick={() => handleOpenAddDialog(peers.length + i)}
                  >
                    {/* Animated gradient border */}
                    <div className="absolute inset-0 bg-gradient-to-br from-[#CA0079] via-[#F1AB11] via-[#CA0079] to-[#F1AB11] animate-gradient-wave rounded-lg"></div>
                    
                    {/* Inner content area */}
                    <div className="absolute inset-[2px] bg-white rounded-lg flex items-center justify-center hover:bg-gray-50 transition-colors">
                      <div className="mx-[20px] my-[10px]">
                        <span className="animate-gradient-text text-center block font-bold">+ Add a Peer</span>
                      </div>
                    </div>
                  </div>
                </AddPeerDialog>
              ))}
            </div>
            
            {peersError && (
              <div className="mt-4 text-red-500">
                {peersError}
              </div>
            )}
            {loadingPeers && (
              <div className="mt-4 text-gray-500">
                Loading peer jurisdictions...
              </div>
            )}
          </div>
          <details className="w-full text-sm text-[rgba(33,33,33,1)] uppercase leading-none mt-6 sm:mt-8 md:mt-[30px] font-inter">
            <summary className="w-full gap-2.5 cursor-pointer hover:text-[#CA0079] transition-colors flex items-center">
              <span className="material-symbols-outlined text-lg sm:text-xl">
                info
              </span>
              HOW WERE MY PEER JURISDICTIONS IDENTIFIED?
            </summary>
            <div className="mt-4 p-4 bg-gray-100 rounded-lg">
              <div className="text-sm sm:text-base normal-case leading-normal text-[#CA0079] font-medium opacity-70">
                We generated peer jurisdictions for {formatPlaceName(selectedPlace?.name || 'your jurisdiction')} based on proximity and similarity in population, population density and housing structure with U.S. Census Survey data from 2023.
              </div>
            </div>
          </details>
        </div>
      </section>

      {/* Step 2 Section with Grey Background */}
      <section className="bg-white w-full">
        <div className="max-w-[1920px] mx-auto px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24 2xl:px-32 py-12 sm:py-16 md:py-20">
          <div className="w-full text-[#212121] font-semibold">
            <div className="font-inter text-sm sm:text-base tracking-[0.32px] uppercase font-semibold leading-[130%]">
              STEP 2
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-[32px] mt-3 leading-[1.3] font-inter">
              View Benchmark Results for {displayName}
            </h2>
            <p className="w-full text-lg sm:text-xl text-[rgba(33,33,33,1)] font-normal mt-3 leading-[1.3] font-inter">
              Find a visual comparison of {selectedPlace?.name || 'your jurisdiction'}'s performance against that of similar regions using 2023 U.S. Census Survey data below. 
              Explore interactive charts and easily share or download your results for broader visibility and impact. 
              You can customize the results by returning to Step 1 and selecting different jurisdictions.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};
